/**
 * Neubo (ROP screening) device constants and helpers.
 *
 * Neubo exams use a clinical data model (gestational age, birth details, risk
 * factors) and an ROP-specific diagnosis (Stage / Zone / Plus Disease) that
 * differ from every other device. Everything Neubo-specific keys off
 * `isNeuboDevice(deviceType)` so other devices remain untouched.
 *
 * Neubo enum fields (riskFactors, birthType, birthOrder, ethnicity) are
 * serialized to their human-readable values by the backend (@JsonValue), so the
 * dashboard receives display-ready strings directly.
 */

export const NEUBO = "NEUBO";

/** Risk-factor characters allowed inline before the rest collapse under "more". */
export const NEUBO_RISK_FACTOR_CHAR_LIMIT = 440;

export function isNeuboDevice(deviceType) {
  if (!deviceType) return false;
  if (Array.isArray(deviceType)) return deviceType.includes(NEUBO);
  return deviceType === NEUBO;
}

/**
 * Diagnosis dropdown options. `value` is the canonical string persisted on the
 * report; `report` is the condensed form rendered in the generated PDF.
 */
export const NEUBO_STAGE_OPTIONS = [
  { value: "Stage 1: Demarcation Line", report: "1 - Demarcation Line" },
  { value: "Stage 2: Ridge", report: "2 - Ridge" },
  {
    value: "Stage 3: Extraretinal Neovascularization",
    report: "3 - Extraretinal Neovascularization"
  },
  {
    value: "Stage 4A: Partial Retinal Detachment (Fovea Attached)",
    report: "4A - Partial Retinal Detachment (Fovea Attached)"
  },
  {
    value: "Stage 4B: Partial Retinal Detachment (Fovea Detached)",
    report: "4B - Partial Retinal Detachment (Fovea Detached)"
  },
  {
    value: "Stage 5A: Open-Funnel Retinal Detachment",
    report: "5A - Open-Funnel Retinal Detachment"
  },
  {
    value: "Stage 5B: Closed-Funnel Retinal Detachment",
    report: "5B - Closed-Funnel Retinal Detachment"
  },
  {
    value:
      "Stage 5C: Closed-Funnel Retinal Detachment with Anterior Segment Abnormalities",
    report:
      "5C - Closed-Funnel Retinal Detachment with Anterior Segment Abnormalities"
  }
];

export const NEUBO_ZONE_OPTIONS = [
  {
    value: "Zone I: Posterior Retina (Most Critical)",
    report: "I - Posterior Retina (Most Critical)"
  },
  { value: "Zone II: Peripheral Retina", report: "II - Peripheral Retina" },
  {
    value: "Zone III: Outermost Peripheral Retina",
    report: "III - Outermost Peripheral Retina"
  },
  {
    value: "Posterior Zone II: 2 Disc Diameters Beyond Zone I",
    report: "Posterior Zone II - 2 Disc Diameters Beyond Zone I"
  }
];

export const NEUBO_PLUS_DISEASE_OPTIONS = [
  {
    value: "Plus Disease: Severe Retinal Vascular Dilation and Tortuosity",
    report: "Plus Disease"
  },
  {
    value: "Preplus Disease: Mild to Moderate Vascular Dilation and Tortuosity",
    report: "Preplus Disease"
  },
  {
    value: "No Plus Disease: Normal or Mild Vascular Appearance",
    report: "No Plus Disease"
  }
];

/** Maps a stored diagnosis value back to its condensed PDF label. */
export function neuboReportLabel(options, value) {
  if (!value) return "";
  const match = options.find(o => o.value === value);
  return match ? match.report : value;
}

// ICD-10 codes from the backend disease catalog (diseases.csv). The catalog
// has a single Retinopathy-of-Prematurity code (H35.1) - there are no
// stage-specific ROP codes - plus the special "NA" (Not Applicable) code.
export const NEUBO_ROP_ICD10 = "H35.1"; // Retinopathy of prematurity (ROP)
export const NEUBO_NO_FINDING_ICD10 = "NA"; // Not Applicable
export const NEUBO_NO_PATHOLOGY_ICD10 = "NP"; // No Pathology Observed

/**
 * Builds the legacy `diagnoses` list for one Neubo eye, using ICD-10 codes the
 * backend actually recognises. The granular Stage/Zone/Plus Disease selections
 * are sent separately (structured fields); here we map the eye to the closest
 * supported catalog code: H35.1 (ROP) when any finding was recorded, otherwise
 * NA. The list is always non-empty because the backend persists reports via
 * Objectify, which reloads empty collections as null - and the amendment
 * analytics path iterates that list without a null check.
 */
export function neuboEyeDiagnoses({
  stage,
  zone,
  plusDisease,
  noPathology
} = {}) {
  if (noPathology) return [NEUBO_NO_PATHOLOGY_ICD10];
  const hasFinding = Boolean(stage || zone || plusDisease);
  return [hasFinding ? NEUBO_ROP_ICD10 : NEUBO_NO_FINDING_ICD10];
}

/** Formats a duration in days as "<wks> wks, <days> days". */
export function formatWeeksDays(days) {
  if (days === null || days === undefined || isNaN(days)) return "-";
  const total = Math.max(0, Math.round(days));
  const weeks = Math.floor(total / 7);
  const remDays = total % 7;
  return `${weeks} wks, ${remDays} days`;
}

/**
 * Post Menstrual Age = gestational age at birth + chronological age.
 * @param {number} gestationalAgeDays gestational age in days (at birth)
 * @param {number} dobEpoch date of birth (epoch ms)
 * @param {number} [asOfEpoch] reference time (epoch ms); defaults to now
 */
export function postMenstrualAge(gestationalAgeDays, dobEpoch, asOfEpoch) {
  if (
    gestationalAgeDays === null ||
    gestationalAgeDays === undefined ||
    isNaN(gestationalAgeDays) ||
    !dobEpoch
  ) {
    return "-";
  }
  const ref = asOfEpoch || Date.now();
  const chronologicalDays = Math.max(0, (ref - dobEpoch) / 86400000);
  return formatWeeksDays(gestationalAgeDays + chronologicalDays);
}

/** Formats birth weight (grams) as "<kg> kg". */
export function formatBirthWeight(grams) {
  if (grams === null || grams === undefined || isNaN(grams)) return "-";
  return `${(grams / 1000).toFixed(1)} kg`;
}

/** Joins a person's first and last name, returning "-" when both are missing. */
export function formatPersonName(firstName, lastName) {
  const name = `${firstName || ""} ${lastName || ""}`.trim();
  return name || "-";
}

/** Joins "Birth Type | Order" handling missing parts. */
export function formatBirthTypeOrder(birthType, birthOrder) {
  if (birthType && birthOrder) return `${birthType} | ${birthOrder}`;
  return birthType || birthOrder || "-";
}

/**
 * Splits risk factors into the chips shown inline (within the character limit)
 * and the remainder hidden behind the "more" control.
 * @returns {{visible: string[], hidden: string[]}}
 */
export function splitRiskFactors(
  riskFactors,
  charLimit = NEUBO_RISK_FACTOR_CHAR_LIMIT
) {
  const factors = Array.isArray(riskFactors) ? riskFactors.filter(Boolean) : [];
  const visible = [];
  const hidden = [];
  let used = 0;
  for (const factor of factors) {
    // account for the joining ", " between chips
    const addition = (visible.length ? 2 : 0) + factor.length;
    if (hidden.length === 0 && used + addition <= charLimit) {
      visible.push(factor);
      used += addition;
    } else {
      hidden.push(factor);
    }
  }
  return { visible, hidden };
}
